import { useCallback, useEffect, useRef, useState } from "react";
import type { DataBlockEventData, ToolStep } from "./useAgentChat";

/**
 * Session readiness, separate from response completion. `compacting` follows
 * a saved response while the server still holds the session lock, so the
 * answer is final but the next message must wait for `idle`.
 */
export type ConversationPhase = "responding" | "compacting" | "idle";

export interface ConversationProgress {
  /** True only while a response is being produced; see {@link normalizeProgress}. */
  active: boolean;
  /** Absent from a backend that predates compaction. */
  phase?: ConversationPhase;
  turn_id: string | null;
  started_at: number | null;
  assistant_message_id: string | null;
  user_message_id: string | null;
  events: { event: string; data: Record<string, unknown> }[];
}

/**
 * Fill `phase` for an older backend and make `active` mean "responding", so a
 * compacting turn reads as completed everywhere `active` is checked.
 */
export function normalizeProgress(
  snapshot: ConversationProgress,
): ConversationProgress {
  const phase = snapshot.phase ?? (snapshot.active ? "responding" : "idle");
  return { ...snapshot, phase, active: phase === "responding" };
}

/** No turn holds the conversation; what a status route without a lease reads as. */
export const IDLE_PROGRESS: ConversationProgress = {
  active: false,
  phase: "idle",
  turn_id: null,
  started_at: null,
  assistant_message_id: null,
  user_message_id: null,
  events: [],
};

/** Rebuild one turn from a checkpoint; never append it to a previous poll. */
export function replayProgress(snapshot: ConversationProgress) {
  return replayProgressEvents(snapshot.events);
}

export function findRunningToolStep(
  steps: ToolStep[],
  data: { tool: unknown; call_id?: unknown },
): number {
  if (typeof data.call_id === "string" && data.call_id) {
    return steps.findIndex(
      (step) => step.status === "running" && step.callId === data.call_id,
    );
  }
  const named = steps.findIndex(
    (step) => step.status === "running" && step.tool === data.tool,
  );
  return named >= 0
    ? named
    : steps.findIndex((step) => step.status === "running");
}

export function replayProgressEvents(events: ConversationProgress["events"]) {
  let content = "";
  let phase: "fetching_context" | "generating" | "tool_executing" =
    "fetching_context";
  const steps: ToolStep[] = [];
  const blocks = new Map<string, DataBlockEventData>();
  for (const { event, data } of events) {
    if (event === "token") {
      content += String(data.content ?? "");
      phase = "generating";
    } else if (
      event === "status" &&
      ["fetching_context", "generating", "tool_executing"].includes(
        String(data.phase),
      )
    ) {
      phase = data.phase as typeof phase;
    } else if (event === "tool_call") {
      steps.push({
        ...(typeof data.call_id === "string" && data.call_id
          ? { callId: data.call_id }
          : {}),
        tool: String(data.tool),
        label: String(data.label),
        status: "running",
        modelTurnNumber:
          typeof data.model_turn_number === "number"
            ? data.model_turn_number
            : undefined,
        durationMs:
          typeof data.duration_ms === "number" ? data.duration_ms : undefined,
      });
      phase = "tool_executing";
    } else if (event === "tool_result") {
      const step =
        steps[
          findRunningToolStep(steps, { tool: data.tool, call_id: data.call_id })
        ];
      if (step) {
        step.status = "done";
        if (typeof data.model_turn_number === "number")
          step.modelTurnNumber = data.model_turn_number;
        step.result = String(data.label ?? "") || undefined;
      }
      if (typeof data.duration_ms === "number") {
        for (const related of steps) {
          if (
            related === step ||
            (data.call_id && related.callId === data.call_id)
          ) {
            related.durationMs = data.duration_ms;
          }
        }
      }
      phase = "generating";
    } else if (event === "skill_invoked") {
      steps.push({
        ...(typeof data.call_id === "string" && data.call_id
          ? { callId: data.call_id }
          : {}),
        tool: `skill:${String(data.name ?? "")}`,
        label: String(data.label ?? `Loaded skill: ${String(data.name ?? "")}`),
        status: "done",
        modelTurnNumber:
          typeof data.model_turn_number === "number"
            ? data.model_turn_number
            : undefined,
        durationMs:
          typeof data.duration_ms === "number" ? data.duration_ms : undefined,
      });
    } else if (event === "data_block") {
      const block = data as unknown as DataBlockEventData;
      blocks.set(block.key, block);
    }
  }
  return { content, phase, steps, blocks };
}

/**
 * Failed status reads retry with exponential backoff (1s, 2s, 4s, 8s). After
 * the last attempt (~15s) the conversation reads as idle so Send is not held
 * forever; the backend lock still refuses a send while a turn really runs, and
 * tab focus starts a fresh check.
 */
const STATUS_RETRY_BASE_MS = 1000;
const STATUS_RETRY_MAX_MS = 8000;
const STATUS_MAX_ATTEMPTS = 5;

export function useConversationProgress({
  conversationId,
  enabled,
  localStreaming,
  fetchStatus,
  onCompleted,
  onInput,
}: {
  conversationId: string;
  enabled: boolean;
  localStreaming: boolean;
  fetchStatus: (
    id: string,
    signal: AbortSignal,
  ) => Promise<ConversationProgress>;
  onCompleted: (id: string, state: ConversationProgress) => Promise<boolean>;
  onInput?: (id: string, state: ConversationProgress) => Promise<boolean>;
}) {
  const [state, setState] = useState<{
    id: string;
    snapshot: ConversationProgress;
  } | null>(null);
  const [checkedId, setCheckedId] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const completed = useRef<string | null>(null);
  const loadedInput = useRef<string | null>(null);
  const reconnect = useCallback(() => setRefresh((n) => n + 1), []);
  /**
   * The tab that streamed this turn saw the server close the response, so the
   * answer is complete: clearing `active` covers a poll that landed between
   * `done` and the close. The close does not say the session is idle — it may
   * be compacting — so `settling` holds the next send until the fresh poll
   * started here answers.
   */
  const [settlingId, setSettlingId] = useState<string | null>(null);
  const settle = useCallback((id: string) => {
    setCheckedId(id);
    setSettlingId(id);
    setRefresh((n) => n + 1);
    setState((prev) =>
      prev?.id === id && prev.snapshot.active
        ? { id, snapshot: { ...prev.snapshot, active: false } }
        : prev,
    );
  }, []);
  useEffect(() => {
    if (!enabled) return;
    if (localStreaming) {
      setCheckedId(null);
      return;
    }
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let polling = false;
    let failures = 0;
    const poll = async () => {
      if (polling || controller.signal.aborted) return;
      clearTimeout(timer);
      polling = true;
      let active = false;
      let compacting = false;
      let failed = false;
      try {
        const snapshot = await fetchStatus(conversationId, controller.signal);
        if (controller.signal.aborted) return;
        active = snapshot.active;
        compacting = snapshot.phase === "compacting";
        if (
          active &&
          snapshot.turn_id &&
          loadedInput.current !== snapshot.turn_id &&
          onInput
        ) {
          if (await onInput(conversationId, snapshot))
            loadedInput.current = snapshot.turn_id;
        }
        if (
          !active &&
          snapshot.turn_id &&
          completed.current !== snapshot.turn_id
        ) {
          // GSI history can lag the lock release. Keep watching until the
          // saved assistant ID is actually present, rather than showing idle.
          if (await onCompleted(conversationId, snapshot))
            completed.current = snapshot.turn_id;
          else active = true;
        }
        if (controller.signal.aborted) return;
        failures = 0;
        setState({ id: conversationId, snapshot: { ...snapshot, active } });
        setCheckedId(conversationId);
      } catch {
        // Preserve the last known running state while retrying a transient
        // outage; past the last attempt, release the conversation as idle.
        failures += 1;
        failed = failures < STATUS_MAX_ATTEMPTS;
        if (!failed && !controller.signal.aborted) {
          setState((prev) =>
            prev?.id === conversationId
              ? {
                  id: conversationId,
                  snapshot: { ...prev.snapshot, active: false, phase: "idle" },
                }
              : prev,
          );
          setCheckedId(conversationId);
        }
      } finally {
        polling = false;
      }
      // Answered or not, stop holding the next send on the close: during a
      // status outage the backend lock is the guard, not this check.
      if (!controller.signal.aborted)
        setSettlingId((prev) => (prev === conversationId ? null : prev));
      // Idle conversations need no watcher. Recheck on opening, returning to
      // the tab, or an explicit reconnect after a local stream completes.
      if (!controller.signal.aborted && (active || compacting || failed))
        timer = setTimeout(
          () => void poll(),
          failed
            ? Math.min(
                STATUS_RETRY_BASE_MS * 2 ** (failures - 1),
                STATUS_RETRY_MAX_MS,
              )
            : 2000,
        );
    };
    const onReturn = () => {
      if (document.visibilityState !== "visible") return;
      failures = 0;
      void poll();
    };
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    void poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [
    conversationId,
    enabled,
    localStreaming,
    fetchStatus,
    onCompleted,
    onInput,
    refresh,
  ]);
  const snapshot = state?.id === conversationId ? state.snapshot : null;
  return {
    snapshot,
    checking: enabled && !localStreaming && checkedId !== conversationId,
    /** The server is compacting after a saved response; sends wait for idle. */
    compacting: enabled && snapshot?.phase === "compacting",
    /** A local turn closed and no poll has confirmed the session is idle. */
    settling: enabled && !localStreaming && settlingId === conversationId,
    reconnect,
    settle,
  };
}
